import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Container, Flex } from '@/ui/layout';
import {
  ENABLED_QUESTS_TO_VIEW_STATUSES_ADMIN,
  ENABLED_STATUSES_INVESTOR,
  STATUS,
  STATUSES_IN_PROGRESS
} from '@/utils/static/kyc';
import { AddQuestionnaireModal } from '@/modules/Questionnaire/modals/AddQuestionnaire';
import { Typography } from 'antd';
import { Button } from '@/components/common/atoms';
import { TableQuestionnaires } from '@/components/common/organisms';
import { useTranslation } from 'react-i18next';
import { adminKYCApi } from '@/api';
import { useSelector } from 'react-redux';
import { CreateButton } from '@/components/common/molecules';
import { usePopups } from '@/hooks';

const isEnabled = (accountStatus, questStatus, adminView) => {
  if (accountStatus !== 'ACTIVE' && !adminView) return false;
  return adminView
    ? ENABLED_QUESTS_TO_VIEW_STATUSES_ADMIN[questStatus]
    : ENABLED_STATUSES_INVESTOR[questStatus];
};

export const ProfileStatusView = ({
  data,
  investor,
  adminView = false,
  rejectedQuestionnaires,
  sumSubStatusRes,
  refetchQuests,
  referralView = false,
  referralId,
  referralToken
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showErrorToast, showSuccessToast } = usePopups();
  const [addQuestVisible, setAddQuestVisible] = useState(false);
  const { isUserVerified } = useSelector((state) => state.investor);

  const sumSubData = useMemo(() => {
    if (sumSubStatusRes && sumSubStatusRes.data) {
      const dataSumSub = sumSubStatusRes.data;

      return {
        ...dataSumSub?.investorVerificationQuestionSection,
        ...(location.state &&
          location.state.verificationSubmitted &&
          dataSumSub?.investorVerificationQuestionSection.status === 'New' && {
            status: 'In process'
          }),
        reviewResult: data?.reviewResult,
        comment: dataSumSub?.comment
      };
    }

    return null;
  }, [sumSubStatusRes, location]);

  const questionnairesSorted = useMemo(() => {
    if (data && data.data) {
      try {
        const questNew = [];
        const questInProgress = [];
        const questPassed = [];
        const questDraft = [];
        const questReject = [];
        let questVerification = null;

        data.data.forEach((quest) => {
          const questStatus = quest.status;
          const error =
            questStatus === STATUS.REJECTED && rejectedQuestionnaires
              ? rejectedQuestionnaires[quest.id]
              : null;
          const formattedQuest = {
            ...quest,
            error: error,
            enabled: isEnabled(investor.accountStatus, questStatus, adminView),
            onClick: (id) => {
              navigate(
                adminView
                  ? `/investor/${investor.id}/questionnaire/${id}`
                  : referralView
                    ? `/referral/${referralId}/questionnaire/${id}`
                    : `/questionnaire/${id}`,
                {
                  state: {
                    prevPath: location.pathname
                  }
                }
              );
            },
            onClickEdit: (id) => {
              navigate(
                adminView
                  ? `/investor/${investor.id}/questionnaire/${id}/edit`
                  : `/questionnaire/${id}`,
                {
                  state: {
                    prevPath: location.pathname
                  }
                }
              );
            },
            onClickDelete: async (id) => {
              try {
                const result = await adminKYCApi.deleteInvestorQuestionnaires(investor.id, id);

                if (result) {
                  await refetchQuests();
                  showSuccessToast('Deleted');
                }
              } catch (e) {
                showErrorToast(e, true);
              }
            }
          };

          if (questStatus === STATUS.NEW) questNew.push(formattedQuest);
          else if (questStatus === STATUS.REJECTED) questReject.push(formattedQuest);
          else if (questStatus === STATUS.QUESTION_UNACCEPTED) questReject.push(formattedQuest);
          else if (questStatus === STATUS.DRAFT) questDraft.push(formattedQuest);
          else if (questStatus === STATUS.PASSED) questPassed.push(formattedQuest);
          else if (STATUSES_IN_PROGRESS[questStatus]) questInProgress.push(formattedQuest);
        });

        if (sumSubData) {
          questVerification = {
            ...sumSubData,
            questName: 'Identification',
            onClick: (id) => {
              navigate(
                adminView ? `/investor/${investor.id}/sumsub-verification` : '/sumsub-verification',
                {
                  state: {
                    prevPath: location.pathname
                  }
                }
              );
            }
          };
        }

        return {
          new: questNew,
          inProgress: questInProgress,
          passed: questPassed,
          draft: questDraft,
          rejected: questReject,
          verification: questVerification
        };
      } catch (e) {}
    }

    return null;
  }, [data, rejectedQuestionnaires, adminView, investor, location]);

  if (questionnairesSorted && investor) {
    return (
      <>
        <Container $paddingY={25} $marginBottom={40}>
          <Flex align={'center'} justify={'space-between'} wrap>
            <Typography.Title level={2}>{t('Questionnaires')}</Typography.Title>

            <Container $smdHidden>
              <Container $width={180}>
                <Button
                  type='filled'
                  text={t('Create questionnairy')}
                  onClick={() => setAddQuestVisible(true)}
                  disabled={
                    adminView
                      ? false
                      : investor.accountStatus !== 'ACTIVE' ||
                        (investor.isAccessibleWithoutVerification ? false : !isUserVerified)
                  }
                />
              </Container>
            </Container>

            <Container $hidden $smdVisible>
              <CreateButton
                onClick={() => setAddQuestVisible(true)}
                disabled={
                  adminView
                    ? false
                    : investor.accountStatus !== 'ACTIVE' ||
                      (investor.isAccessibleWithoutVerification ? false : !isUserVerified)
                }
              />
            </Container>
          </Flex>

          <Container $marginTop={36}>
            <TableQuestionnaires
              adminView={adminView}
              disabled={investor.isAccessibleWithoutVerification ? false : !isUserVerified}
              data={questionnairesSorted}
              getFuncFromObj
            />
          </Container>

          <AddQuestionnaireModal
            visible={addQuestVisible}
            onClose={setAddQuestVisible}
            user={adminView ? 'admin' : 'investor'}
            onSuccess={refetchQuests}
            referralToken={referralToken}
          />
        </Container>
      </>
    );
  }

  return null;
};
